import { ReactNode, useRef } from 'react';
import Button from 'components/button';
import { ENTERPRISE_PLAN_URL } from 'constants/reporting';
import enterpriseHero from './assets/enterprise-hero.png';
import { ReactComponent as MetricSparkline } from './assets/metric-sparkline.svg';
import {
  ArtworkColumn,
  ArtworkFigure,
  ArtworkImage,
  CheckBadge,
  Container,
  ContentColumn,
  CtaGroup,
  Description,
  Divider,
  Eyebrow,
  EyebrowLabel,
  FeatureList,
  FeatureListItem,
  FeatureListLabel,
  FeaturesSection,
  Header,
  Headline,
  HighlightedText,
  MetricCard,
  MetricLabel,
  MetricValue,
  MetricsGrid,
  Sparkline,
} from './styles';

export interface EnterpriseUpsellFeature {
  id: string;
  label: string;
}

export interface EnterpriseUpsellMetric {
  id: string;
  quantity: string | number;
  /** Static, trusted copy only. */
  label: string;
  /** Renders the decorative sparkline below the value (design: "Video watch time"). */
  sparkline?: boolean;
}

// Feature-list check: the Figma feather check is filled with the Secondary/Promo
// Purple gradient (#863ad2 -> #5936e4). CSS `fill` cannot take a gradient, so the
// glyph references an SVG <linearGradient> def by id. The id is generated per
// component instance so multiple upsells on one page never collide (React 17 has
// no useId).
let checkGradientSeq = 0;

const CheckGradientDef = ({ id }: { id: string }): JSX.Element => (
  <svg width="0" height="0" aria-hidden="true" style={{ position: 'absolute' }}>
    <defs>
      <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="3.58%" stopColor="#863ad2" />
        <stop offset="97.14%" stopColor="#5936e4" />
      </linearGradient>
    </defs>
  </svg>
);

const CheckMark = ({ gradientId }: { gradientId: string }): JSX.Element => (
  <svg width="13" height="11" viewBox="0 0 13 11" fill="none" aria-hidden="true">
    <path
      d="M11.0426 1.02893C11.3258 0.695792 11.8254 0.655283 12.1585 0.938451C12.4917 1.22162 12.5322 1.72124 12.249 2.05437L5.51985 9.97104C5.23224 10.3094 4.72261 10.3451 4.3907 10.05L0.828197 6.88335C0.50141 6.59288 0.471975 6.09249 0.762452 5.7657C1.05293 5.43891 1.55332 5.40948 1.88011 5.69995L4.83765 8.32889L11.0426 1.02893Z"
      fill={`url(#${gradientId})`}
    />
  </svg>
);

export interface EnterpriseUpsellProps {
  eyebrow?: string;
  headline: ReactNode;
  description?: ReactNode;
  features?: EnterpriseUpsellFeature[];
  /** Defaults to the bundled Enterprise hero illustration. Pass "" to omit it. */
  artworkSrc?: string;
  artworkAlt?: string;
  metrics?: EnterpriseUpsellMetric[];
  layout?: 'split' | 'stacked';
  contactSalesLabel?: string;
  /** Page the "Contact sales" CTA links to (opens in a new tab). */
  contactSalesHref?: string;
  viewEnterpriseLabel?: string;
  /** Enterprise plan page the "View Enterprise plan" CTA links to (opens in a new tab). */
  viewEnterpriseHref?: string;
  className?: string;
}

const EnterpriseUpsell = ({
  eyebrow,
  headline,
  description,
  features,
  artworkSrc = enterpriseHero,
  artworkAlt = 'Enterprise plan illustration',
  metrics,
  layout = 'split',
  contactSalesLabel = 'Contact sales',
  contactSalesHref = 'https://learn.ine.com/talk-to-a-team-advisor',
  viewEnterpriseLabel = 'View Enterprise plan',
  viewEnterpriseHref = ENTERPRISE_PLAN_URL,
  className,
}: EnterpriseUpsellProps): JSX.Element => {
  const hasFeatures = Boolean(features && features.length > 0);
  const hasMetrics = Boolean(metrics && metrics.length > 0);
  const hasArtwork = Boolean(artworkSrc);
  const hasArtworkColumn = hasArtwork || hasMetrics;

  // Stable, unique-per-instance id for the feature-check gradient def.
  const gradientIdRef = useRef<string>('');
  if (!gradientIdRef.current) {
    checkGradientSeq += 1;
    gradientIdRef.current = `enterpriseUpsellCheckGradient-${checkGradientSeq}`;
  }
  const checkGradientId = gradientIdRef.current;

  const metricCards = metrics?.map((metric) => (
    <MetricCard key={metric.id} $inline={!metric.sparkline}>
      <MetricLabel>{metric.label}</MetricLabel>
      <MetricValue>{metric.quantity}</MetricValue>
      {metric.sparkline && (
        <Sparkline data-testid="enterprise-metric-sparkline">
          <MetricSparkline aria-hidden="true" focusable="false" />
        </Sparkline>
      )}
    </MetricCard>
  ));

  return (
    <Container $layout={layout} data-layout={layout} className={className}>
      <ContentColumn $layout={layout}>
        <Header>
          {eyebrow && (
            <Eyebrow>
              <EyebrowLabel>{eyebrow}</EyebrowLabel>
            </Eyebrow>
          )}
          <Headline>{headline}</Headline>
        </Header>
        {description && <Description>{description}</Description>}
        <CtaGroup>
          {contactSalesHref && (
            <Button
              $primary
              size="small"
              icon="ExternalLink"
              href={contactSalesHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              {contactSalesLabel}
            </Button>
          )}
          {viewEnterpriseHref && (
            <Button
              $secondary
              size="small"
              href={viewEnterpriseHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              {viewEnterpriseLabel}
            </Button>
          )}
        </CtaGroup>
        {hasFeatures && (
          <>
            <Divider />
            <FeaturesSection>
              <CheckGradientDef id={checkGradientId} />
              <FeatureListLabel>Enterprise plan unlocks</FeatureListLabel>
              <FeatureList>
                {features?.map((feature) => (
                  <FeatureListItem key={feature.id}>
                    <CheckBadge aria-hidden="true">
                      <CheckMark gradientId={checkGradientId} />
                    </CheckBadge>
                    {feature.label}
                  </FeatureListItem>
                ))}
              </FeatureList>
            </FeaturesSection>
          </>
        )}
      </ContentColumn>
      {hasArtworkColumn && (
        <ArtworkColumn>
          {hasArtwork ? (
            <ArtworkFigure>
              <ArtworkImage src={artworkSrc} alt={artworkAlt} />
              {hasMetrics && <MetricsGrid $overlay>{metricCards}</MetricsGrid>}
            </ArtworkFigure>
          ) : (
            hasMetrics && <MetricsGrid>{metricCards}</MetricsGrid>
          )}
        </ArtworkColumn>
      )}
    </Container>
  );
};

export { HighlightedText };
export default EnterpriseUpsell;
